import { of, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { forEach, size } from 'lodash';

import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { Broadcaster } from '@core/app/scripts/services/broadcaster';
import { Customer } from '@shared/app/scripts/services/customer';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Persist } from '@core/app/scripts/services/persist';

/*
 * Component responsible for /customer route.
 */
angular
  .module('posApp')
  .directive(
    'customer',
    (
      broadcaster: Broadcaster,
      customer: Customer,
      displayMessage: DisplayMessage,
      escapeRegexFilter,
      loadCustomerInfo,
      lowercaseFilter,
      navigateTo: NavigateTo,
      persist: Persist,
      pos,
      scheduler,
      translate: ng.translate.ITranslateService,
      uppercaseFilter: ng.IFilterUppercase,
    ) => ({
      restrict: 'E',

      scope: true,

      template: require('./customer.html').default,

      link: function ($scope: any) {
        const $translate = translate;

        $scope.selectedRow = null;
        $scope.selectedLine = -1;
        $scope.filterValues = {};

        // TODO add unit tests
        $scope.acls = pos.config.acls;

        $scope.listLoading = true;

        const reloadCustomerSubject = new Subject();

        const arrFilter = { active: true },
          arrSort = [['last_name', 1]],
          arrColumns = [
            'cust_id',
            'addresses.zipcode',
            'addresses.street',
            'addresses.houseadd',
            'addresses.houseno',
            'last_name',
            'addresses.city',
            'addresses.primary',
            'loyalty_no',
            'first_name',
            'middle_name',
            'contacts.phone',
            'contacts.mobile',
            'contacts.email',
            'contacts.name',
            'contacts.primary',
            'title',
          ];

        function loadCustomers(filter, columns, sortArgument) {
          let sort = sortArgument;

          if (size(filter) == 1) {
            sort = [];
          }

          customer.filter(filter, 0, 50, undefined, sort).then(
            (data) => {
              $scope.pagelist = data;

              /* If there are results set the selection to the first line
               * If there are no results set selected line to the default value -1
               */
              if (data && data.length) {
                $scope.selectCurrentLine(0, data[0]);
              } else {
                $scope.selectCurrentLine(-1, null);
              }

              $scope.listLoading = false;
            },
            () => {
              $scope.listLoading = false;
              $scope.pagelist = [];
            },
          );
        }

        // Initial load without ordering
        loadCustomers(arrFilter, arrColumns, arrSort);

        $scope.gridLines = 11;

        $scope.focusStreams = [of(undefined)];

        function getColumns() {
          return [
            {
              name: 'zipcode',
              title: 'zipcode',
              label: $translate.instant('zipcode'),
              factor: 1,
              checked: true,
              pattern: /.{4,}/,
              maxlength: 10,
            },
            {
              name: 'houseno',
              title: 'houseno',
              label: $translate.instant('houseno'),
              factor: 1,
              checked: true,
              pattern: /.*/,
              maxlength: 10,
              isDisabled: () => {
                if (
                  $scope.filterValues.zipcode &&
                  $scope.filterValues.zipcode.length > 3
                ) {
                  return false;
                }

                if (
                  $scope.filterValues.address &&
                  $scope.filterValues.address.length > 3
                ) {
                  return false;
                }

                return true;
              },
            },
            {
              name: 'last_name',
              title: 'last name',
              label: $translate.instant('last name'),
              factor: 2.5,
              checked: true,
              pattern: /.{2,}/,
              maxlength: 10,
            },
            {
              name: 'first_name',
              title: 'First name',
              label: $translate.instant('First name'),
              factor: 2,
              checked: false,
              pattern: /.{2,}/,
              maxlength: 10,
            },
            {
              name: 'middle_name',
              title: 'Middle name',
              label: $translate.instant('Middle name'),
              factor: 1,
              checked: false,
              pattern: /.*/,
              maxlength: 10,
              isDisabled: () => true,
            },
            {
              name: 'title',
              title: 'Titel',
              label: $translate.instant('Titel'),
              factor: 0.5,
              checked: false,
              pattern: /.*/,
              maxlength: 10,
              isDisabled: () => true,
            },
            {
              name: 'address',
              title: 'Address',
              label: $translate.instant('Address'),
              factor: 2.5,
              checked: true,
              pattern: /.{3,}/,
              maxlength: 10,
            },
            {
              name: 'city',
              title: 'City',
              label: $translate.instant('City'),
              factor: 1.5,
              checked: true,
              pattern: /.{3,}/,
              maxlength: 10,
            },
            {
              name: 'customerno',
              title: 'customerno',
              label: $translate.instant('customerno'),
              factor: 1,
              checked: true,
              pattern: /.{4,}/,
              maxlength: 15,
            },
            {
              name: 'c_phone',
              title: 'Contact phone',
              label: $translate.instant('Contact phone'),
              factor: 1.5,
              checked: false,
              pattern: /.{6,}/,
              maxlength: 10,
            },
            {
              name: 'c_email',
              title: 'Contact email',
              label: $translate.instant('Contact email'),
              factor: 2.5,
              checked: false,
              pattern: /.{4,}/,
              maxlength: 10,
            },
            {
              name: 'c_mobile',
              title: 'Contact mobile',
              label: $translate.instant('Contact mobile'),
              factor: 1.5,
              checked: false,
              pattern: /.{6,}/,
              maxlength: 10,
            },
          ];
        }

        $scope.columns = [];
        $scope.openColumnPopup = false;
        $scope.width = 1;
        $scope.pagelist = [];
        let totColFactor = 0;

        $scope.$on('pageShow', () => {
          //Update the smart list after the DOM is created
          $scope.setFocusFirstField();
        });

        let i;

        // Set the column values
        $scope.columns = getColumns();

        // Take the checked values from localStorage and load them into the column object
        if (persist.get('customerGridColumns')) {
          const storedColumns = JSON.parse(persist.get('customerGridColumns'));
          if (typeof storedColumns[0] === 'boolean') {
            for (i in $scope.columns) {
              $scope.columns[i].checked = !!storedColumns[i];
            }
          } else {
            // This was the old way as an array of objects, now we only store booleans
            for (i in storedColumns) {
              if (storedColumns[i].checked) {
                $scope.columns[i].checked = storedColumns[i].checked;
              }
            }
          }
        } else {
          $scope.columns = getColumns();
        }

        /*
         * Calculate the total column factor (used to resize the columns proportionally)
         */
        function calculateTotalCoulumnFactor() {
          totColFactor = 0;
          for (const i in $scope.columns) {
            if ($scope.columns[i].checked) {
              totColFactor += $scope.columns[i].factor;
            }
          }
        }

        $scope.filterColumns = () => {
          const storedValues = [];
          //Add the columns that are checked in the list to the columns filter
          for (const s in $scope.columns) {
            storedValues.push($scope.columns[s].checked);
          }
          persist.set('customerGridColumns', JSON.stringify(storedValues));
          calculateTotalCoulumnFactor();
        };

        //Add the default shop to the shop filter
        $scope.filterColumns();

        // Force the page list to resize the first time when viewing.
        // Otherwise the controls fall out of view
        broadcaster.send('forceFitToBottom');

        $scope.setFocusFirstField = () => {
          if ($scope.columns && $scope.columns[0] && $scope.columns[0].name) {
            for (const i in $scope.columns) {
              if ($scope.columns[i].checked) {
                broadcaster.send('setFocus', $scope.columns[i].name);
                break;
              }
            }
          }
        };

        $scope.navigateTo = (view) => {
          navigateTo(view);
        };

        $scope.getClass = (ind) => {
          if (ind === $scope.selectedLine) {
            return 'ui-btn-active';
          } else {
            return 'inactive';
          }
        };

        $scope.toShow = (ind) => {
          if ($scope.columns.length > 0) {
            if ($scope.columns && $scope.columns[ind].checked) {
              return $scope.columns[ind].checked;
            }
          }
        };

        $scope.getStyle = (ind) => {
          let colWidth = 0,
            colWidthText = '0%',
            i,
            gridRowRatio;

          if ($scope.columns[ind] && $scope.columns[ind].checked) {
            colWidth = ($scope.columns[ind].factor / totColFactor) * 100;
            colWidthText = colWidth.toFixed(2) + '%';
          }

          return {
            width: colWidthText,
          };
        };

        $scope.selectCurrentLine = (ind, currentLine) => {
          $scope.selectedRow = currentLine;
          $scope.toggleSelect(ind);
        };

        $scope.toggleSelect = (ind) => {
          $scope.selectedLine = ind;
        };

        // select customer from the list
        $scope.selectClient = () => {
          if ($scope.selectedRow) {
            pos.addCustomerToTransaction({
              id: $scope.selectedRow._id,
            });
          }
          navigateTo('pos.route#pos');

          pos.selectedMenu = 1;
        };

        // select customer from the list
        $scope.deleteCustomer = () => {
          if ($scope.selectedRow) {
            displayMessage.message({
              message: $translate.instant('delete_customer_question'),
              type: 'prompt',
              callback: (result) => {
                if (result === 'yes') {
                  $scope.selectedRow.active = false;

                  return customer.save($scope.selectedRow).then(
                    () => {
                      reloadCustomerSubject.next({
                        filter: arrFilter,
                        columns: arrColumns,
                        sort: arrSort,
                      });
                    },
                    () => {
                      displayMessage.message({
                        message: $translate.instant('delete_customer_failed'),
                        type: 'error',
                      });
                    },
                  );
                }
              },
            });
          }
        };

        $scope.addNew = () => {
          loadCustomerInfo('pos.route#customer');
          navigateTo('pos.route#customer-info/add-new');
        };

        $scope.openCustomerInfo = (id) => {
          if (id) {
            loadCustomerInfo('pos.route#customer');
            navigateTo('pos.route#customer-info/' + id);
          } else {
            displayMessage.message({
              message: $translate.instant('open_customer_failed'),
              type: 'error',
            });
          }
        };

        $scope.custSearch = (filterInput) => {
          let addressPrimary = false,
            contactPrimary = false;

          forEach($scope.columns, (column) => {
            if (column.isDisabled && column.isDisabled()) {
              $scope.filterValues[column.name] = '';
            }
          });

          // Only search for active customers
          arrFilter = { active: true };

          if (filterInput.zipcode) {
            arrFilter['customer_zipcode'] = {
              $regex:
                '^' +
                uppercaseFilter(String(filterInput.zipcode).replace(' ', '')),
            };
          }

          if (filterInput.houseno) {
            // that's very slippery. How come we know what they actually write in the field
            arrFilter['addresses.houseno'] = {
              $regex: '^' + uppercaseFilter(filterInput.houseno),
            };
            addressPrimary = true;
          }

          if (filterInput.address) {
            arrFilter['customer_street'] = {
              $regex:
                '^' + escapeRegexFilter(uppercaseFilter(filterInput.address)),
            };
          }

          if (filterInput.city) {
            arrFilter['customer_city'] = {
              $regex: '^' + uppercaseFilter(filterInput.city),
            };
          }

          if (filterInput.customerno) {
            arrFilter['loyalty_no'] = {
              $regex: '^' + filterInput.customerno.replace('.', ''),
            };
          }

          if (filterInput.first_name) {
            arrFilter['first_name'] = {
              $regex: '^' + escapeRegexFilter(filterInput.first_name),
            };
          }

          if (filterInput.last_name) {
            arrFilter['last_name'] = {
              $regex: '^' + escapeRegexFilter(filterInput.last_name),
            };
          }

          if (filterInput.c_phone) {
            arrFilter['contacts.phone'] = {
              $regex: '^' + escapeRegexFilter(filterInput.c_phone),
            };
            contactPrimary = true;
          }

          if (filterInput.c_email) {
            arrFilter['contacts.email'] = {
              $regex:
                '^' + escapeRegexFilter(lowercaseFilter(filterInput.c_email)),
            };
            contactPrimary = true;
          }

          if (filterInput.c_mobile) {
            arrFilter['contacts.mobile'] = {
              $regex: '^' + escapeRegexFilter(filterInput.c_mobile),
            };
            contactPrimary = true;
          }

          if (addressPrimary) {
            arrFilter['addresses.primary'] = true;
          }

          if (contactPrimary) {
            arrFilter['contacts.primary'] = true;
          }

          reloadCustomerSubject.next({
            filter: arrFilter,
            columns: arrColumns,
            sort: arrSort,
          });
        };

        const customerReload$ = reloadCustomerSubject
          .asObservable()
          .pipe(debounceTime(300, scheduler));

        customerReload$.subscribe(({ filter, columns, sort }) => {
          loadCustomers(filter, columns, sort);
        });

        $scope.$on('$destroy', () => {
          reloadCustomerSubject.complete();
        });

        $scope.isPrimary = (arrAddress) => {
          if (arrAddress) {
            for (let i = 0; i < arrAddress.length; i++) {
              if (arrAddress[i].primary) {
                return i;
              }
            }
          }
        };

        $scope.refreshCustomerGrid = () => {
          $scope.filterColumns();
        };

        $scope.selectAllColumns = () => {
          for (const s in $scope.columns) {
            $scope.columns[s].checked = $scope.selectAll;
          }
          $scope.filterColumns();
        };

        $scope.toggleColumnPopup = () => {
          $scope.openColumnPopup = $scope.openColumnPopup ? false : true;
        };

        $scope.setFocusFirstField();
      },
    }),
  );
